import * as React from 'react';
import { Button, Empty, Input, Modal, Radio, Select, Switch, Typography, notification } from 'antd';
import { useParams } from 'react-router-dom';
import { useCampaignAvatarMap, useCampaignPosts, useCampaignQuery, useDeleteCampaignDrafts, useDeleteCampaignPost, useExcludeCampaignPost, useIncludeCampaignPost, useUpdateCampaignKeywords } from '../queryHooks';
import PostCard from '../components/PostCard';
import type { CampaignPost } from '../types';

const ContentSection: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const campaignId = id ? Number(id) : undefined;
    const [tab, setTab] = React.useState<'published' | 'draft'>('published');
    const [sort, setSort] = React.useState('recent');
    const [search, setSearch] = React.useState('');
    const [inCampaign, setInCampaign] = React.useState(true);
    const { data: campaign } = useCampaignQuery(campaignId);
    const avatars = useCampaignAvatarMap(campaignId);
    const saveKeywords = useUpdateCampaignKeywords();
    const keywords = (campaign?.hashtags ?? []).map((h) => h.tag);
    const include = useIncludeCampaignPost();
    const exclude = useExcludeCampaignPost();
    const remove = useDeleteCampaignPost();
    const removeDrafts = useDeleteCampaignDrafts();
    const { data: posts = [], isLoading } = useCampaignPosts(campaignId, {
        status: tab === 'published' ? 'published' : 'draft',
        sort: sort === 'likes' ? 'likes' : sort === 'vit' ? 'vit' : 'recent',
        search: search || undefined,
        in_campaign: tab === 'published' ? String(inCampaign) : undefined,
    });

    const onInclude = async (post: CampaignPost) => {
        if (!campaignId) return;
        try {
            await include.mutateAsync({ campaignId, postId: post.id });
        } catch (e: any) {
            notification.error({ message: e?.response?.data?.error || 'Could not add post' });
        }
    };
    const onExclude = async (post: CampaignPost) => {
        if (!campaignId) return;
        try {
            await exclude.mutateAsync({ campaignId, postId: post.id });
        } catch (e: any) {
            notification.error({ message: e?.response?.data?.error || 'Could not remove post' });
        }
    };
    const onDelete = async (post: CampaignPost) => {
        if (!campaignId) return;
        try {
            await remove.mutateAsync({ campaignId, postId: post.id });
        } catch (e: any) {
            notification.error({ message: e?.response?.data?.error || 'Could not delete post' });
        }
    };

    const onRemoveDrafts = () => {
        if (!campaignId) return;
        Modal.confirm({
            title: 'Remove all drafts?',
            content: 'This deletes every draft on this campaign, including posts you removed from the campaign.',
            okText: 'Remove all drafts',
            okButtonProps: { danger: true },
            onOk: async () => {
                try {
                    const res = await removeDrafts.mutateAsync(campaignId);
                    notification.success({
                        message: res.deleted ? `Removed ${res.deleted} draft${res.deleted === 1 ? '' : 's'}` : 'No drafts to remove',
                        placement: 'bottomRight',
                        duration: 2,
                    });
                } catch (e: any) {
                    notification.error({ message: e?.response?.data?.error || 'Could not remove drafts' });
                }
            },
        });
    };

    const onKeywords = async (next: string[]) => {
        if (!campaignId) return;
        const tags = next.map((t) => t.replace(/^#/, '').trim()).filter(Boolean);
        try {
            await saveKeywords.mutateAsync({ id: campaignId, tags });
        } catch (e: any) {
            notification.error({ message: e?.response?.data?.error || 'Could not save keywords' });
        }
    };

    const keywordField = (
        <div style={{ marginBottom: 16 }}>
            <Typography.Text strong style={{ display: 'block', marginBottom: 4 }}>Keywords</Typography.Text>
            <Select
                mode="tags"
                tokenSeparators={[',']}
                placeholder="e.g. ΗΒΗ, lidl"
                value={keywords}
                onChange={onKeywords}
                loading={saveKeywords.isLoading}
                style={{ width: '100%', maxWidth: 560 }}
            />
            <Typography.Text type="secondary" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
                Add them in any caps. ΗΒΗ, Ήβη, and ήβη are saved as the same keyword. Posts and stories that do not contain a keyword are saved as drafts. Hit Refresh to apply.
            </Typography.Text>
        </div>
    );

    const tabs = (
        <Radio.Group
            value={tab}
            onChange={(e) => setTab(e.target.value)}
            optionType="button"
            buttonStyle="solid"
            options={[{ label: 'Published', value: 'published' }, { label: 'Drafts', value: 'draft' }]}
        />
    );

    const grid = (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
            {posts.map((p) => (
                <PostCard
                    key={p.id}
                    post={p}
                    avatar={avatars[p.creator_id]}
                    draft={tab === 'draft'}
                    onInclude={onInclude}
                    onExclude={onExclude}
                    onDelete={onDelete}
                    busy={include.isLoading || exclude.isLoading || remove.isLoading}
                />
            ))}
        </div>
    );

    if (tab === 'draft') {
        return (
            <div>
                {keywordField}
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                    {tabs}
                    <Button danger loading={removeDrafts.isLoading} onClick={onRemoveDrafts}>
                        Remove all drafts
                    </Button>
                </div>
                {posts.length === 0 && !isLoading ? (
                    <Empty description="No drafts. Posts and stories that do not match a keyword, and posts you removed from the campaign, appear here." />
                ) : grid}
            </div>
        );
    }

    return (
        <div>
            {keywordField}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                {tabs}
                <Input.Search
                    allowClear
                    placeholder="Search caption or creator"
                    style={{ width: 220 }}
                    onSearch={setSearch}
                    onChange={(e) => { if (!e.target.value) setSearch(''); }}
                />
                <Select
                    value={sort}
                    onChange={setSort}
                    style={{ width: 160 }}
                    options={[
                        { label: 'Most recent', value: 'recent' },
                        { label: 'Highest VIT', value: 'vit' },
                        { label: 'Most likes', value: 'likes' },
                    ]}
                />
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <Switch size="small" checked={inCampaign} onChange={setInCampaign} />
                    <Typography.Text type="secondary">In campaign</Typography.Text>
                </span>
            </div>
            {posts.length === 0 && !isLoading ? (
                <Empty description={inCampaign
                    ? 'No matching posts yet. Add keywords on the campaign and hit Refresh.'
                    : 'No removed posts. Hover a post and choose Remove from campaign.'}
                />
            ) : grid}
        </div>
    );
};

export default ContentSection;
